import { DashboardDataQuery } from '../DashboardData.generated';

import { extractDateKey, filterByTimeframe } from './scoreProductFilters';
import { DailyUniqueIssuesData } from './transformResults';

/**
 * Filters score_web_products by timeframe and groups by date
 * @param products - Array of score_web_products items
 * @param timeframe - Timeframe in days to filter by
 * @returns Map of date keys to aggregated counts
 */
export const filterByTimeframeAndDate = (
	products: DashboardDataQuery['scoreWebProductsDaily' | 'scoreWebProductsDailyTotal'],
	timeframe?: number
): Map<string, { total: number; critical: number; serious: number }> => {
	const filtered = filterByTimeframe<
		DashboardDataQuery['scoreWebProductsDaily' | 'scoreWebProductsDailyTotal'][number]
	>(products || [], timeframe);
	const groupedByDate = new Map<string, { total: number; critical: number; serious: number }>();

	filtered.forEach((product) => {
		const date = product.calculation_date;
		if (date) {
			const { issues: total, issues_seriouse: critical, issues_critical: serious } = product;

			const dateKey = extractDateKey(date);
			const existing = groupedByDate.get(dateKey) || { total: 0, critical: 0, serious: 0 };

			groupedByDate.set(dateKey, {
				total: existing.total + (total ?? 0),
				critical: existing.critical + (critical ?? 0),
				serious: existing.serious + (serious ?? 0)
			});
		}
	});

	return groupedByDate;
};

/**
 * Converts grouped date map to sorted array of DailyUniqueIssuesData
 * @param groupedByDate - Map of date keys to aggregated counts
 * @returns Sorted array of daily unique issues data
 */
export const groupByDateAndSum = (
	groupedByDate: Map<string, { total: number; critical: number; serious: number }>
): DailyUniqueIssuesData[] => {
	return Array.from(groupedByDate.entries())
		.map(([dateStr, counts]) => ({
			date: dateStr,
			total: counts.total,
			critical: counts.critical,
			serious: counts.serious
		}))
		.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};
